"use client";

import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface Post {
  slug: string;
  title: string;
  category: string;
  date: string;
  readTime: string;
  description: string;
}

const posts: Post[] = [
  {
    slug: "node-memory-leaks",
    title: "Deep-Dive: Node.js Memory Leaks and V8 Heap Profiling",
    category: "Performance",
    date: "July 24, 2026",
    readTime: "8 min read",
    description: "Diagnosing garbage collection anomalies, tracing event listener allocation roots, and executing Chrome DevTools snapshots inside running production containers.",
  },
  {
    slug: "websocket-scaling",
    title: "System Design: Scaling WebSockets using Redis Pub/Sub Backplanes",
    category: "Architecture",
    date: "May 18, 2026",
    readTime: "12 min read",
    description: "Resolving stateless proxy route boundaries and coordinating horizontal client notification broadcasts across distinct WebSocket application instances.",
  },
  {
    slug: "traefik-deployments",
    title: "DevOps: Zero-Downtime Container Deployments via Traefik Ingress Routing",
    category: "DevOps",
    date: "March 05, 2026",
    readTime: "6 min read",
    description: "Configuring rolling health endpoints, injecting environment keys, and hot-swapping running Docker nodes without dropping active HTTP sessions.",
  }
];

export default function BlogPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-32 pb-20 relative z-10">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.04)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] -z-10 opacity-60" />
        
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-4 block font-mono">
              Archives
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-white font-[var(--font-poppins)] tracking-tighter mb-4">
              Technical <span className="gradient-text">Writing</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base font-medium max-w-xl mx-auto">
              Deep-dives into systems engineering, backend scaling anomalies, database bottlenecks, and automated deployments setups.
            </p>
          </div>

          <div className="space-y-8">
            {posts.map((post, idx) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white/[0.02] border border-white/5 rounded-[2rem] p-8 hover:border-indigo-500/30 transition-all duration-300 shadow-2xl flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4 font-mono text-[10px]">
                    <span className="px-2.5 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-bold uppercase tracking-wider">
                      {post.category}
                    </span>
                    <span className="text-gray-500">{post.readTime}</span>
                  </div>

                  <Link href={`/blog/${post.slug}`} className="group">
                    <h2 className="text-2xl font-black text-white group-hover:text-indigo-400 transition-colors duration-300 font-[var(--font-poppins)] tracking-tight leading-snug mb-3">
                      {post.title}
                    </h2>
                  </Link>

                  <p className="text-gray-400 text-sm leading-relaxed mb-6 font-medium">
                    {post.description}
                  </p>
                </div>

                <div className="flex items-center justify-between border-t border-white/5 pt-4 font-mono text-[10px] text-gray-500">
                  <span>{post.date}</span>
                  <Link href={`/blog/${post.slug}`} className="flex items-center gap-2 font-black uppercase tracking-widest text-indigo-400 hover:text-white transition-colors">
                    <span>Read Article</span>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
